/**
 * Approval Packages — list view. Cross-artifact (every type lives here).
 *
 * Compliance officers see every package in their org. Filters: status,
 * artifact-type, "needs my attention" (quorum reachable + I'm the creator).
 * Optimized to look like the inbox of someone who runs a tight ship — not
 * an admin form.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';

const API = process.env.REACT_APP_API_URL;

const STATUS_CFG = {
  draft:     { label: 'Draft',          dot: 'bg-slate-400',    chip: 'bg-slate-100 text-slate-600 border-slate-200' },
  sent:      { label: 'Out for review', dot: 'bg-indigo-500',   chip: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  approved:  { label: 'Approved',       dot: 'bg-emerald-500',  chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected:  { label: 'Rejected',       dot: 'bg-red-500',      chip: 'bg-red-50 text-red-600 border-red-200' },
  expired:   { label: 'Expired',        dot: 'bg-slate-300',    chip: 'bg-slate-100 text-slate-500 border-slate-200' },
  cancelled: { label: 'Cancelled',      dot: 'bg-slate-300',    chip: 'bg-slate-100 text-slate-500 border-slate-200' },
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function fmtRelativeDeadline(d) {
  if (!d) return '—';
  const ms = new Date(d).getTime() - Date.now();
  const days = Math.round(ms / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days > 1)   return `in ${days} days`;
  if (days === -1) return 'yesterday';
  return `${Math.abs(days)} days ago`;
}

export default function ApprovalsHome({ onOpenPackage, onCreatePackage }) {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [artifactTypes, setArtifactTypes] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [alerts, setAlerts] = useState([]);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [ackBusy, setAckBusy] = useState(null);

  // Load known artifact types so the chip set is data-driven (Phase 2 adds
  // coa_work_plan, Phase 3 adds more).
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/approvals/artifact-types`, { credentials: 'include' });
        const d = await r.json();
        if (d.success) setArtifactTypes(d.artifact_types || []);
      } catch {}
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : '';
      const res = await fetch(`${API}/api/approvals/packages${qs}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setPackages(data.packages || []);
    } catch {}
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Open cascade alerts — for the bell badge.
  const loadAlerts = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/approvals/cascade-alerts`, { credentials: 'include' });
      const d = await r.json();
      if (d.success) setAlerts(d.alerts || []);
    } catch {}
  }, []);
  useEffect(() => {
    loadAlerts();
    const id = setInterval(loadAlerts, 60000); // refresh every minute
    return () => clearInterval(id);
  }, [loadAlerts]);

  const ackAlert = async (id) => {
    setAckBusy(id);
    try {
      const r = await fetch(`${API}/api/approvals/cascade-alerts/${id}/ack`, {
        method: 'POST', credentials: 'include',
      });
      const d = await r.json();
      if (d.success) setAlerts((prev) => prev.filter((a) => a.id !== id));
    } catch {}
    setAckBusy(null);
  };

  // Client-side filter for type + search (list endpoint is small).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return packages.filter((p) => {
      if (q && !p.name?.toLowerCase().includes(q) && !p.description?.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [packages, search]);

  const counts = useMemo(() => {
    const c = { all: packages.length };
    Object.keys(STATUS_CFG).forEach((s) => { c[s] = packages.filter((p) => p.status === s).length; });
    return c;
  }, [packages]);

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="text-[11px] uppercase font-bold tracking-wider text-indigo-600">All sign-offs · cross-module</div>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight mt-0.5">Packages awaiting sign-off</h2>
            <p className="text-slate-500 text-sm mt-1 max-w-2xl">
              Every committee, CEO, and board sign-off — policies, work plans, attestations — in one inbox.
              Every decision is captured to the same audit trail.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Cascade-alerts bell — surfaces stale approvals (source artifact
                has a newer version since sign-off). Click for the slide-over. */}
            <button
              onClick={() => setAlertsOpen(true)}
              className="relative inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm"
              title={alerts.length === 0 ? 'No stale approvals' : `${alerts.length} stale approval${alerts.length === 1 ? '' : 's'}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082A23.848 23.848 0 0018 16.5c-1.5-1.5-2.5-3-2.5-6V9.75a3.75 3.75 0 10-7.5 0v.75c0 3-1 4.5-2.5 6a23.848 23.848 0 003.143.582m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"/>
              </svg>
              {alerts.length > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
                  {alerts.length > 9 ? '9+' : alerts.length}
                </span>
              )}
            </button>
            <button
              onClick={onCreatePackage}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-sm transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              New sign-off package
            </button>
          </div>
        </div>

        {/* Search + filters */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-5">
          <div className="flex items-center gap-3 mb-3">
            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" /></svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search packages by name or description…"
              className="flex-1 text-sm border-0 focus:outline-none focus:ring-0 placeholder:text-slate-400 bg-transparent"
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap border-t border-slate-100 pt-3">
            <FilterChip active={!statusFilter} onClick={() => setStatusFilter('')} label="All" count={counts.all} />
            {Object.entries(STATUS_CFG).map(([s, cfg]) => (
              <FilterChip
                key={s}
                active={statusFilter === s}
                onClick={() => setStatusFilter(s)}
                label={cfg.label}
                count={counts[s] || 0}
                dot={cfg.dot}
              />
            ))}
            {artifactTypes.length > 1 && (
              <>
                <span className="text-slate-300 mx-1">·</span>
                <FilterChip active={!typeFilter} onClick={() => setTypeFilter('')} label="All types" />
                {artifactTypes.map((t) => (
                  <FilterChip
                    key={t.artifact_type}
                    active={typeFilter === t.artifact_type}
                    onClick={() => setTypeFilter(t.artifact_type)}
                    label={t.label}
                  />
                ))}
              </>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-400 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState onCreate={onCreatePackage} hasAny={packages.length > 0} />
        ) : (
          <div className="grid gap-3">
            {filtered.map((p) => (
              <PackageRow key={p.id} pkg={p} onOpen={() => onOpenPackage(p.id)} />
            ))}
          </div>
        )}
      </div>

      {/* Cascade-alerts slide-over */}
      {alertsOpen && (
        <div className="fixed inset-0 z-40 flex justify-end" onClick={() => setAlertsOpen(false)}>
          <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" />
          <aside
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-white shadow-2xl border-l border-slate-200 overflow-y-auto"
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-indigo-600 font-bold">Cascade alerts</div>
                <h3 className="text-base font-bold text-slate-900">
                  {alerts.length === 0 ? 'No stale approvals' : `${alerts.length} stale approval${alerts.length === 1 ? '' : 's'}`}
                </h3>
              </div>
              <button onClick={() => setAlertsOpen(false)} className="text-slate-400 hover:text-slate-700 text-2xl leading-none">×</button>
            </div>
            <div className="p-5">
              {alerts.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-4xl mb-2">✅</div>
                  <div className="text-sm text-slate-700 font-medium">Every approved artifact is still current.</div>
                  <div className="text-xs text-slate-500 mt-1">We check daily for source-row drift after sign-off.</div>
                </div>
              ) : (
                <ul className="space-y-3">
                  {alerts.map((a) => (
                    <li key={a.id} className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <div className="text-xs font-bold text-amber-800 uppercase tracking-wider">
                        Approval is now stale
                      </div>
                      <div className="text-sm font-semibold text-slate-900 mt-1">{a.package_name}</div>
                      <div className="text-xs text-slate-600 mt-1">
                        {a.successor_summary || `A new version of the ${a.artifact_type.replace(/_/g, ' ')} this package approved has appeared.`}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-2">
                        Approved {fmtDate(a.finalized_at)} · alert raised {fmtDate(a.created_at)}
                      </div>
                      <div className="mt-3 flex justify-end">
                        <button
                          onClick={() => ackAlert(a.id)}
                          disabled={ackBusy === a.id}
                          className="text-xs px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold disabled:opacity-50"
                        >
                          {ackBusy === a.id ? 'Acknowledging…' : 'Acknowledge'}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, label, count, dot }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border transition-all ${
        active
          ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm'
          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
      }`}
    >
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-white' : dot}`} />}
      {label}
      {count != null && (
        <span className={`text-[10px] ${active ? 'text-indigo-100' : 'text-slate-400'}`}>{count}</span>
      )}
    </button>
  );
}

function PackageRow({ pkg, onOpen }) {
  const cfg = STATUS_CFG[pkg.status] || STATUS_CFG.draft;
  const decided = Number(pkg.decided_count || 0);
  const total = Number(pkg.recipient_count || 0);
  const pct = total ? Math.round((decided / total) * 100) : 0;
  const deadlinePast = pkg.deadline_at && new Date(pkg.deadline_at) < new Date() && pkg.status === 'sent';
  return (
    <button
      onClick={onOpen}
      className="text-left bg-white border border-slate-200 rounded-2xl px-5 py-4 hover:border-indigo-300 hover:shadow-sm transition-all"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-900 truncate">{pkg.name}</span>
            <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-bold uppercase tracking-wider ${cfg.chip}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
              {cfg.label}
            </span>
          </div>
          {pkg.description && (
            <div className="text-xs text-slate-500 mt-1 truncate">{pkg.description}</div>
          )}
          <div className="flex items-center gap-4 mt-2 text-[11px] text-slate-500 flex-wrap">
            <span>📑 {pkg.item_count} {pkg.item_count === 1 ? 'item' : 'items'}</span>
            <span>👥 {decided}/{total} decided</span>
            <span className={deadlinePast ? 'text-red-600 font-semibold' : ''}>
              ⏰ Deadline {fmtRelativeDeadline(pkg.deadline_at)}
            </span>
            <span className="text-slate-400">Created {fmtDate(pkg.created_at)}</span>
          </div>
        </div>
        <div className="w-24 flex-shrink-0">
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full ${pkg.status === 'approved' ? 'bg-emerald-500' : pkg.status === 'rejected' ? 'bg-red-400' : 'bg-indigo-500'}`} style={{ width: `${pct}%` }} />
          </div>
          <div className="text-[10px] text-slate-400 text-right mt-1">{pct}%</div>
        </div>
      </div>
    </button>
  );
}

function EmptyState({ onCreate, hasAny }) {
  return (
    <div className="bg-white border border-dashed border-slate-200 rounded-2xl py-16 text-center">
      <div className="text-4xl mb-2">{hasAny ? '🔎' : '📬'}</div>
      <div className="text-slate-800 font-semibold">{hasAny ? 'Nothing matches your filters' : 'No sign-off packages yet'}</div>
      <div className="text-slate-400 text-sm mt-1 mb-5 max-w-md mx-auto">
        {hasAny
          ? 'Try a different status or clear the search.'
          : 'Send any policy, work plan, or attestation to your committee, CEO, or board — and watch decisions land in real time.'}
      </div>
      {!hasAny && (
        <button
          onClick={onCreate}
          className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-sm"
        >
          New sign-off package
        </button>
      )}
    </div>
  );
}
